import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoggerService } from '@/common/infrastructure/services/LoggerService';
import { organizationTimezoneService } from '@/common/infrastructure/services/OrganizationTimezoneService';
import { TypeaheadOption } from '@/common/presentation/components/inputs/Typeahead/index';
import { useTimeZoneTypeaheadFetch } from '@/jobs/application/adapters/listTimeZonesAutocomplete';
import { JobFormData } from '@/jobs/application/types/JobTypes';
import timezoneMapping from './timezoneMapping.json';

// Create a logger instance for this hook
const logger = LoggerService.create('useJobTimezone');

// Define the interface for timezone data
interface TimezoneData {
  timeZone: string;
}

// Type alias for timezone options with proper typing
type TimezoneOption = TypeaheadOption<string>;

interface UseJobTimezoneProps {
  rawJob?: {
    id?: number;
    timeZone?: string | null;
  } | null;
  preventUserTimezoneFallback?: boolean; // New flag to prevent user timezone fallback
}

// Utility function to detect user's timezone and map it to backend format
const detectUserTimezone = (): string | null => {
  try {
    // Get the user's timezone from the browser
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!userTimezone) {
      logger.warn('Unable to detect user timezone from browser');
      return null;
    }

    return userTimezone;
  } catch (error) {
    logger.error('Error detecting user timezone', error);
    return null;
  }
};

// Helper function to find timezone by offset match
const findTimezoneByOffset = (
  userOffsetHours: number,
  availableTimezones: TimezoneOption[],
  userTimezone: string
): TimezoneOption | null => {
  const matchingTimezones = availableTimezones.filter((tz) => {
    const tzData = tz.data as unknown as TimezoneData;
    if (!tzData?.timeZone) return false;

    // More flexible regex pattern to handle single-digit hours (e.g., UTC+5:30)
    const utcMatch = tzData.timeZone?.match(/\(UTC([+-]\d{1,2}):\d{2}\)/);
    if (!utcMatch || !utcMatch[1]) return false;

    const tzOffsetHours = parseInt(utcMatch[1]);
    // Allow for 1.5 hour tolerance to handle 30-minute offsets and edge cases
    return Math.abs(tzOffsetHours - userOffsetHours) <= 1.5;
  });

  if (matchingTimezones.length === 0) return null;

  // Try to find one with a similar name first
  const nameMatch = matchingTimezones.find((tz) => {
    const tzData = tz.data as unknown as TimezoneData;
    // More robust name matching: extract city name and check for inclusion
    const userCity = userTimezone.split('/').pop()?.toLowerCase() || '';
    const tzName = tzData?.timeZone?.toLowerCase() || '';
    return userCity && tzName.includes(userCity);
  });

  return nameMatch || matchingTimezones[0] || null;
};

// Function to find the best matching timezone from the backend
const findBestMatchingTimezone = (userTimezone: string, availableTimezones: TimezoneOption[]): TimezoneOption | null => {
  // Try exact match from mapping
  const mappedTimezoneId = (timezoneMapping as Record<string, string>)[userTimezone];

  if (mappedTimezoneId) {
    const exactMatch = availableTimezones.find((tz) => tz.value === mappedTimezoneId);
    if (exactMatch) {
      return exactMatch;
    }
  }

  // Try to find by name matching (for cases where mapping doesn't work)
  const userCity = userTimezone.split('/').pop()?.toLowerCase() || '';

  if (userCity) {
    const nameMatch = availableTimezones.find((tz) => {
      const tzData = tz.data as unknown as TimezoneData;
      const tzName = tzData?.timeZone?.toLowerCase() || '';
      const tzLabel = tz.label.toLowerCase();
      const tzValue = tz.value.toLowerCase();

      return tzName.includes(userCity) || tzLabel.includes(userCity) || tzValue.includes(userCity);
    });

    if (nameMatch) {
      return nameMatch;
    }
  }

  // If no exact match, try to find by timezone offset
  try {
    const now = new Date();
    const userOffset = now.getTimezoneOffset(); // Returns offset in minutes
    const userOffsetHours = -userOffset / 60; // Convert to hours (negative because getTimezoneOffset returns opposite sign)

    const offsetMatch = findTimezoneByOffset(userOffsetHours, availableTimezones, userTimezone);
    if (offsetMatch) {
      return offsetMatch;
    }
  } catch (error) {
    logger.error('Error finding matching timezone by offset', error);
  }

  return null;
};

export const useJobTimezone = ({ rawJob, preventUserTimezoneFallback = false }: UseJobTimezoneProps) => {
  const { values, setFieldValue } = useFormikContext<JobFormData>();
  const [initialTimeZone, setInitialTimeZone] = useState<TimezoneOption | null>(null);
  const [timezoneLoaded, setTimezoneLoaded] = useState(false);
  const [userHasSelectedTimezone, setUserHasSelectedTimezone] = useState(false);
  const [lastSearchResults, setLastSearchResults] = useState<TimezoneOption[]>([]);
  const [lastSearchTerm, setLastSearchTerm] = useState<string>('');
  const userTimezoneDetectionAttempted = useRef(false);
  const lastLoadedTimezoneIdRef = useRef<string | null>(null);
  const lastDisplayedFetchIdRef = useRef<string | null>(null);

  // Reset state when rawJob changes (different job loaded)
  useEffect(() => {
    // Always reset state when rawJob changes to ensure clean state for new job
    setInitialTimeZone(null);
    setTimezoneLoaded(false);
    setUserHasSelectedTimezone(false);
    setLastSearchResults([]);
    setLastSearchTerm('');
    userTimezoneDetectionAttempted.current = false;
  }, [rawJob?.id]);

  const {
    fetchTimeZoneById,
    fetchTimeZonesBySearch,
    initialTimeZones,
    error: timezoneError,
    isLoading: timezoneLoading,
    clearError: clearTimezoneError
  } = useTimeZoneTypeaheadFetch();

  // Memoize user timezone detection to avoid repeated calculations
  // Only detect user timezone if not prevented by jobsite selection
  const userLocalTimezone = useMemo(() => {
    if (preventUserTimezoneFallback) {
      return null;
    }
    const timezone = detectUserTimezone();
    return timezone ? findBestMatchingTimezone(timezone, initialTimeZones) : null;
  }, [initialTimeZones, preventUserTimezoneFallback]);

  // Helper functions to break down complex caching logic
  const shouldReturnCachedResults = (searchTerm: string, lastSearchResults: TimezoneOption[], lastSearchTerm: string): boolean => {
    return !searchTerm?.trim() && lastSearchResults.length > 0 && lastSearchTerm.trim().length > 0;
  };

  const shouldIncludeUserTimezone = (
    userHasSelectedTimezone: boolean,
    userLocalTimezone: TimezoneOption | null,
    timezones: TimezoneOption[]
  ): boolean => {
    if (userHasSelectedTimezone || !userLocalTimezone) return false;
    return !timezones.some((tz) => tz.value === userLocalTimezone.value);
  };

  const getTimezonesWithUserTimezone = useCallback(
    (timezones: TimezoneOption[]): TimezoneOption[] => {
      if (shouldIncludeUserTimezone(userHasSelectedTimezone, userLocalTimezone, timezones)) {
        return [...timezones, userLocalTimezone!];
      }
      return timezones;
    },
    [userHasSelectedTimezone, userLocalTimezone]
  );

  const fetchInitialTimezones = useCallback(async (): Promise<TimezoneOption[]> => {
    try {
      const searchResults = await fetchTimeZonesBySearch('');
      return searchResults;
    } catch (error) {
      logger.error('Failed to fetch initial timezones', error);
      return [];
    }
  }, [fetchTimeZonesBySearch]);

  // Function that combines initial timezones with search
  const fetchTimeZonesWithInitial = useCallback(
    async (searchTerm: string) => {
      // If no search term or empty search, return initial timezones with user timezone logic
      if (!searchTerm || searchTerm.trim().length === 0) {
        if (shouldReturnCachedResults(searchTerm, lastSearchResults, lastSearchTerm)) {
          return lastSearchResults;
        }

        // If initialTimeZones is not loaded yet, fetch some initial data
        if (initialTimeZones.length === 0) {
          return await fetchInitialTimezones();
        }

        return getTimezonesWithUserTimezone(initialTimeZones);
      }

      const searchResults = await fetchTimeZonesBySearch(searchTerm);

      // Store the search results and term for future use
      setLastSearchResults(searchResults);
      setLastSearchTerm(searchTerm);

      return searchResults;
    },
    [fetchTimeZonesBySearch, initialTimeZones, lastSearchResults, lastSearchTerm, fetchInitialTimezones, getTimezonesWithUserTimezone]
  );

  // Get the effective timezone from either rawJob or Formik values
  const effectiveTimezone = rawJob?.timeZone !== undefined ? rawJob?.timeZone : values.timeZone;

  // Check if this is a new job (no ID) or an existing job (has ID)
  const isNewJob = !rawJob?.id;

  // Helper function to determine if timezone should be loaded
  const shouldLoadTimezone = (
    effectiveTimezone: string | null | undefined,
    userHasSelectedTimezone: boolean,
    isNewJob: boolean,
    initialTimeZone: TimezoneOption | null
  ): boolean => {
    if (!effectiveTimezone || effectiveTimezone === '') {
      return false;
    }

    if (!isNewJob) {
      // For existing jobs, only fetch when we don't have the corresponding initial timezone cached yet
      return !initialTimeZone || initialTimeZone.value !== effectiveTimezone;
    }

    // For new jobs, only load if no timezone is already set and user hasn't selected one
    return initialTimeZone === null && !userHasSelectedTimezone;
  };

  // Load initial timezone when rawJob or Formik values change
  // Also trigger when initialTimeZones are loaded to handle cases where rawJob loads first
  useEffect(() => {
    const loadInitialTimeZone = async () => {
      const shouldLoad = shouldLoadTimezone(effectiveTimezone, userHasSelectedTimezone, isNewJob, initialTimeZone);

      if (shouldLoad && effectiveTimezone) {
        if (lastLoadedTimezoneIdRef.current === effectiveTimezone) {
          return;
        }
        try {
          const timezone = await fetchTimeZoneById(effectiveTimezone);
          setInitialTimeZone(timezone);
          setTimezoneLoaded(true);
          lastLoadedTimezoneIdRef.current = effectiveTimezone;
        } catch (error) {
          logger.error('Failed to fetch timezone by ID', error);
          setInitialTimeZone(null);
          setTimezoneLoaded(true);
          lastLoadedTimezoneIdRef.current = effectiveTimezone;
        }
      } else if (!effectiveTimezone && timezoneLoaded) {
        setInitialTimeZone(null);
        setTimezoneLoaded(false);
        lastLoadedTimezoneIdRef.current = null;
      }
    };

    loadInitialTimeZone();
  }, [
    effectiveTimezone,
    fetchTimeZoneById,
    timezoneLoaded,
    userHasSelectedTimezone,
    rawJob?.id, // Use only ID instead of entire object to prevent unnecessary re-renders
    rawJob?.timeZone, // Also track timeZone changes
    initialTimeZones.length,
    initialTimeZone,
    isNewJob
  ]);

  // Check localStorage for stored timezone preference on new jobs
  useEffect(() => {
    if (isNewJob && !initialTimeZone && !userHasSelectedTimezone && initialTimeZones.length > 0) {
      const storedTimezone = organizationTimezoneService.getOrganizationDefaultTimezone();
      if (storedTimezone) {
        // Try exact match first in initialTimeZones
        const exactMatch = initialTimeZones.find((tz) => tz.value === storedTimezone);
        if (exactMatch) {
          setInitialTimeZone(exactMatch);
          setTimezoneLoaded(true);
          return;
        }

        // If not found in initialTimeZones, fetch it by ID to get the correct timezone
        const fetchStoredTimezone = async () => {
          try {
            const timezoneOption = await fetchTimeZoneById(storedTimezone);
            if (timezoneOption) {
              setInitialTimeZone(timezoneOption);
              setTimezoneLoaded(true);
              // Also sync with Formik if there's no current value
              if (!values.timeZone) {
                setFieldValue('timeZone', timezoneOption.value);
              }
            } else {
              logger.warn('Stored timezone not found in system', { storedTimezone });
            }
          } catch (error) {
            logger.error('Failed to fetch stored timezone', { storedTimezone, error });
          }
        };

        fetchStoredTimezone();
      }
    }
  }, [isNewJob, initialTimeZone, userHasSelectedTimezone, initialTimeZones, fetchTimeZoneById, values.timeZone, setFieldValue]);

  // Reset initialTimeZone when user clears selection to prevent automatic re-selection
  useEffect(() => {
    // Only reset if the user has previously selected a timezone and now cleared it
    if (!values.timeZone && userHasSelectedTimezone && initialTimeZone) {
      setInitialTimeZone(null);
    }
  }, [values.timeZone, userHasSelectedTimezone, initialTimeZone]);

  /**
   * Determines whether the timezone should be synchronized with Formik based on the current state.
   *
   * This function handles the complex logic of when to automatically set the timezone field value,
   * considering different scenarios for new jobs vs existing jobs and user interactions.
   *
   * @param isNewJob - Whether this is a new job (no ID) or existing job (has ID)
   * @param effectiveTimezone - The effective timezone from either rawJob or Formik values
   * @param valuesTimeZone - The current timezone value in Formik
   * @param userHasSelectedTimezone - Whether the user has manually selected a timezone
   * @param initialTimeZone - The detected/loaded initial timezone option
   * @returns boolean indicating whether the timezone should be synchronized
   */
  const shouldSyncTimezone = (
    isNewJob: boolean,
    effectiveTimezone: string | null | undefined,
    valuesTimeZone: string | null | undefined,
    userHasSelectedTimezone: boolean,
    initialTimeZone: TimezoneOption | null
  ): boolean => {
    // Base conditions that must be met for any synchronization
    if (!initialTimeZone || valuesTimeZone || userHasSelectedTimezone) {
      return false;
    }

    // For existing jobs: Always sync if the field is empty and we have an initial timezone
    // This ensures existing jobs get their timezone properly loaded from the database
    if (!isNewJob) {
      return true;
    }

    // For new jobs: Only sync if there's no effective timezone
    // This prevents overriding user's explicit clearing of the field
    //
    // Scenario 1: User hasn't interacted with the field yet (effectiveTimezone is empty/null)
    //   -> Sync the detected timezone to provide a good default
    //
    // Scenario 2: User explicitly cleared the field (effectiveTimezone is empty string)
    //   -> Don't sync to respect user's choice to clear the field
    //
    // Scenario 3: User manually selected a timezone (effectiveTimezone has a value)
    //   -> Don't sync to avoid overriding user's selection
    return !effectiveTimezone;
  };

  // Syncs the Formik field value with the loaded initialTimeZone
  // This ensures the field is updated both for new jobs and existing jobs
  // But only when no user-defined value is already set
  useEffect(() => {
    if (shouldSyncTimezone(isNewJob, effectiveTimezone, values.timeZone, userHasSelectedTimezone, initialTimeZone)) {
      setFieldValue('timeZone', initialTimeZone!.value);
    }
  }, [initialTimeZone, values.timeZone, setFieldValue, userHasSelectedTimezone, isNewJob, effectiveTimezone]);

  // Listen for changes to the timezone field value and update initialTimeZone accordingly
  // This handles cases where the timezone is set programmatically (e.g., from jobsite)
  useEffect(() => {
    if (values.timeZone) {
      if (lastDisplayedFetchIdRef.current === values.timeZone) {
        return;
      }
      // If there's a timezone value in the form but no initialTimeZone, or if it doesn't match, fetch it
      // This ensures initialTimeZone stays in sync when timezone is set programmatically
      const needsUpdate = !initialTimeZone || initialTimeZone.value !== values.timeZone;

      if (needsUpdate) {
        const fetchAndSetInitialTimezone = async () => {
          try {
            const currentTz = values.timeZone!;
            lastDisplayedFetchIdRef.current = currentTz;
            const timezoneOption = await fetchTimeZoneById(currentTz);
            if (timezoneOption) {
              setInitialTimeZone(timezoneOption);
            }
          } catch (error) {
            logger.error('Failed to fetch timezone for display', { timezoneId: values.timeZone, error });
          }
        };

        fetchAndSetInitialTimezone();
      }
    }
  }, [values.timeZone, initialTimeZone, fetchTimeZoneById]);

  // Function to handle when user manually selects a timezone
  const handleTimezoneSelection = useCallback(
    (selectedTimezone: TimezoneOption | null) => {
      if (selectedTimezone) {
        // Check if the selected timezone is the user's local timezone
        const isUserLocalTimezone = userLocalTimezone?.value === selectedTimezone.value;

        // Only mark as user-selected if it's not their local timezone
        if (!isUserLocalTimezone) {
          setUserHasSelectedTimezone(true);
        }

        setFieldValue('timeZone', selectedTimezone.value);

        // Clear search results when user selects an item
        setLastSearchResults([]);
        setLastSearchTerm('');
      } else {
        // When user clears the selection:
        // 1. Reset the flag so user timezone can be included again
        // 2. Clear the field value
        // 3. Reset the initialTimeZone to prevent automatic re-selection
        // 4. Clear search results
        setUserHasSelectedTimezone(false);
        setFieldValue('timeZone', null);
        setInitialTimeZone(null);
        setLastSearchResults([]);
        setLastSearchTerm('');
      }
    },
    [setFieldValue, userLocalTimezone]
  );

  return {
    userTimezone: userLocalTimezone,
    initialTimeZone,
    timezoneError,
    timezoneLoading,
    clearTimezoneError,
    fetchTimeZonesWithInitial,
    handleTimezoneSelection
  };
};
